import { useEffect, useState, Fragment, useMemo } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { Card, Modal, ModalBody, ModalHeader } from "reactstrap";
import AddressForm from "./AddressForm";
import AddressRow from "./AddressRow";
import AddressHeader from "./AddressHeader";
import Pagination from "../../components/utils/Pagination";
import Loading from "../../components/utils/Loading";
import { useTranslation } from "react-i18next";
import { formattedRoleAddress } from "../../components/formatted/FormattedText";
import EmptyState from "../../components/EmptyState";
import StandardTable from "../../ui/components/StandardTable";
import StandardTableFilterDropdown from "../../ui/components/StandardTableFilterDropdown";

const useOpenPreset = (setAddFormData, setModalCreate, defaultData) => {
  return {
    openPreset: (preset) => {
      setAddFormData(preset.data);
      setModalCreate(true);
    },
    openCustom: () => {
      setAddFormData({ ...defaultData, label: "", value: "" });
      setModalCreate(true);
    },
  };
};

const Address = ({ token, API, onCreateSite, onOpenSite }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const openCreate = searchParams.get("openCreate");

  const PRESETS = [
    {
      key: t("Sorting plant"),
      label: t("Sorting plant"),
      icon: "♻️",
      color: "#22A35F",
      data: {
        name: t("Sorting plant") + " A",
        role: "sorting_plant",
        number: "",
        street: "",
        postalcode: 0,
        city: "",
        location: [0, 0],
        opening: {
          0: [
            ["07:00", "12:00"],
            ["12:00", "17:00"],
          ],
          1: [
            ["07:00", "12:00"],
            ["12:00", "17:00"],
          ],
          2: [
            ["07:00", "12:00"],
            ["12:00", "17:00"],
          ],
          3: [
            ["07:00", "12:00"],
            ["12:00", "17:00"],
          ],
          4: [
            ["07:00", "12:00"],
            ["12:00", "17:00"],
          ],
          5: [
            ["07:00", "12:00"],
            ["12:00", "17:00"],
          ],
          6: [
            ["07:00", "12:00"],
            ["12:00", "17:00"],
          ],
        },
        openingTimezone: "Europe/Paris",
      },
    },
    {
      key: t("Building site"),
      label: t("Building site"),
      icon: "🚧",
      color: "#FF9F1C",
      data: {
        name: t("Building site") + " A",
        role: "building_site",
        number: "",
        street: "",
        postalcode: 0,
        city: "",
        location: [0, 0],
        opening: {
          0: [
            ["07:00", "12:00"],
            ["12:00", "17:00"],
          ],
          1: [
            ["07:00", "12:00"],
            ["12:00", "17:00"],
          ],
          2: [
            ["07:00", "12:00"],
            ["12:00", "17:00"],
          ],
          3: [
            ["07:00", "12:00"],
            ["12:00", "17:00"],
          ],
          4: [
            ["07:00", "12:00"],
            ["12:00", "17:00"],
          ],
          5: [
            ["07:00", "12:00"],
            ["12:00", "17:00"],
          ],
          6: [
            ["07:00", "12:00"],
            ["12:00", "17:00"],
          ],
        },
        openingTimezone: "Europe/Paris",
      },
    },
    {
      key: t("Dump"),
      label: t("Dump"),
      icon: "🚛",
      color: "#C97A40",
      data: {
        name: t("Dump") + " A",
        role: "dump",
        number: "",
        street: "",
        postalcode: 0,
        city: "",
        location: [0, 0],
        opening: {
          0: [
            ["09:00", "12:00"],
            ["14:00", "18:00"],
          ],
          1: [
            ["09:00", "12:00"],
            ["14:00", "18:00"],
          ],
          2: [
            ["09:00", "12:00"],
            ["14:00", "18:00"],
          ],
          3: [
            ["09:00", "12:00"],
            ["14:00", "18:00"],
          ],
          4: [
            ["09:00", "12:00"],
            ["14:00", "18:00"],
          ],
          5: [
            ["09:00", "12:00"],
            ["14:00", "18:00"],
          ],
          6: [
            ["09:00", "12:00"],
            ["14:00", "18:00"],
          ],
        },
        openingTimezone: "Europe/Paris",
      },
    },
    {
      key: t("Industrial site"),
      label: t("Industrial site"),
      icon: "🏭",
      color: "#6C757D",
      data: {
        name: t("Industrial site") + " A",
        role: "industrial_site",
        number: "",
        street: "",
        postalcode: 0,
        city: "",
        location: [0, 0],
        opening: {
          0: [
            ["09:00", "12:00"],
            ["14:00", "18:00"],
          ],
          1: [
            ["09:00", "12:00"],
            ["14:00", "18:00"],
          ],
          2: [
            ["09:00", "12:00"],
            ["14:00", "18:00"],
          ],
          3: [
            ["09:00", "12:00"],
            ["14:00", "18:00"],
          ],
          4: [
            ["09:00", "12:00"],
            ["14:00", "18:00"],
          ],
          5: [
            ["09:00", "12:00"],
            ["14:00", "18:00"],
          ],
          6: [
            ["09:00", "12:00"],
            ["14:00", "18:00"],
          ],
        },
        openingTimezone: "Europe/Paris",
      },
    },
    {
      key: t("Landfill site"),
      label: t("Landfill site"),
      icon: "⛰️",
      color: "#8D6E63",
      data: {
        name: t("Landfill site") + " A",
        role: "landfill_site",
        number: "",
        street: "",
        postalcode: 0,
        city: "",
        location: [0, 0],
        opening: {
          0: [
            ["07:00", "12:00"],
            ["12:00", "17:00"],
          ],
          1: [
            ["07:00", "12:00"],
            ["12:00", "17:00"],
          ],
          2: [
            ["07:00", "12:00"],
            ["12:00", "17:00"],
          ],
          3: [
            ["07:00", "12:00"],
            ["12:00", "17:00"],
          ],
          4: [
            ["07:00", "12:00"],
            ["12:00", "17:00"],
          ],
          5: [
            ["07:00", "12:00"],
            ["12:00", "17:00"],
          ],
          6: [
            ["07:00", "12:00"],
            ["12:00", "17:00"],
          ],
        },
        openingTimezone: "Europe/Paris",
      },
    },
    {
      key: t("Incineration centre"),
      label: t("Incineration centre"),
      icon: "🔥",
      color: "#E63946",
      data: {
        name: t("Incineration centre") + " A",
        role: "incineration_centre",
        number: "",
        street: "",
        postalcode: 0,
        city: "",
        location: [0, 0],
        opening: {
          0: [
            ["09:00", "12:00"],
            ["14:00", "18:00"],
          ],
          1: [
            ["09:00", "12:00"],
            ["14:00", "18:00"],
          ],
          2: [
            ["09:00", "12:00"],
            ["14:00", "18:00"],
          ],
          3: [
            ["09:00", "12:00"],
            ["14:00", "18:00"],
          ],
          4: [
            ["09:00", "12:00"],
            ["14:00", "18:00"],
          ],
          5: [
            ["09:00", "12:00"],
            ["14:00", "18:00"],
          ],
          6: [
            ["09:00", "12:00"],
            ["14:00", "18:00"],
          ],
        },
        openingTimezone: "Europe/Paris",
      },
    },
    {
      key: t("Engineering consultancy"),
      label: t("Engineering consultancy"),
      icon: "📑",
      color: "#00A7A6",
      data: {
        name: t("Engineering consultancy") + " A",
        role: "engineering_consultancy",
        number: "",
        street: "",
        postalcode: 0,
        city: "",
        location: [0, 0],
        opening: {
          0: [
            ["09:00", "12:00"],
            ["14:00", "18:00"],
          ],
          1: [
            ["09:00", "12:00"],
            ["14:00", "18:00"],
          ],
          2: [
            ["09:00", "12:00"],
            ["14:00", "18:00"],
          ],
          3: [
            ["09:00", "12:00"],
            ["14:00", "18:00"],
          ],
          4: [
            ["09:00", "12:00"],
            ["14:00", "18:00"],
          ],
          5: [
            ["09:00", "12:00"],
            ["14:00", "18:00"],
          ],
          6: [
            ["09:00", "12:00"],
            ["14:00", "18:00"],
          ],
        },
        openingTimezone: "Europe/Paris",
      },
    },
  ];

  const defaultData = {
    name: "",
    number: "",
    street: "",
    postalcode: 0,
    city: "",
    location: [0, 0],
    opening: {
      0: [
        ["", ""],
        ["", ""],
      ], //Sunday
      1: [
        ["", ""],
        ["", ""],
      ], //Monday
      2: [
        ["", ""],
        ["", ""],
      ], //Tuesday
      3: [
        ["", ""],
        ["", ""],
      ], //Wednesday
      4: [
        ["", ""],
        ["", ""],
      ], //Thursday
      5: [
        ["", ""],
        ["", ""],
      ], //Friday
      6: [
        ["", ""],
        ["", ""],
      ], //Saturday
    },
    openingTimezone: "Europe/Paris",
  };

  const [tableData, setTableData] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [totalEntry, setTotalEntry] = useState();
  const [totalEntryWithoutFilter, setTotalEntryWithoutFilter] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordPerPage, setRecordPerPage] = useState(10);
  const [resultPage, setResultPage] = useState(0);
  const [isDeleted, setIsDeleted] = useState(false);
  const [keywordFilter, setKeywordFilter] = useState("");
  const [addFormData, setAddFormData] = useState(defaultData);
  const [formData, setFormData] = useState(defaultData);
  const [modalCreate, setModalCreate] = useState(openCreate ? true : false);
  const [sortByField, setSortByField] = useState("name");
  const [orderSortByField, setOrderSortByField] = useState("1");
  const [roleKeys, setRoleKeys] = useState([""]);
  const [listRoleFilter, setListRoleFilter] = useState({});
  const [modalEdit, setModalEdit] = useState(false);
  const [showPresetModal, setShowPresetModal] = useState(false);

  const { openPreset, openCustom } = useOpenPreset(
    setAddFormData,
    setModalCreate,
    defaultData,
  );

  const hasActiveFilters = useMemo(() => {
    return (
      Boolean(keywordFilter) ||
      (listRoleFilter?.selectedList && listRoleFilter.selectedList.length > 0)
    );
  }, [keywordFilter, listRoleFilter]);

  useEffect(() => {
    const fetchData = async () => {
      const response = await axios.get(
        `${API}address?page=${currentPage}&limit=${recordPerPage}`,
        {
          headers: {
            authorization: "Bearer " + token,
          },
          params: {
            keywordFilter: keywordFilter,
            listPostalCodeFilter: {},
            listCityFilter: {},
            sortByField: sortByField,
            orderSortByField: orderSortByField,
            listRoleFilter: listRoleFilter,
          },
        },
      );
      setTotalEntry(response.data.count);
      setTableData(response.data.data);
      setRoleKeys(response.data.roleKeys);
      if (!hasActiveFilters) {
        setTotalEntryWithoutFilter(response.data.count);
      }
      setIsLoading(false);
      setIsDeleted(false);
    };
    fetchData();
  }, [
    token,
    API,
    currentPage,
    recordPerPage,
    isDeleted,
    listRoleFilter,
    keywordFilter,
    sortByField,
    orderSortByField,
    hasActiveFilters,
  ]);

  const hasAddresses = Array.isArray(tableData) && tableData.length > 0;
  const hasAnyAddresses = totalEntryWithoutFilter > 0;
  const showEmptyState = !hasAnyAddresses && !hasActiveFilters;
  const isSettingsList = typeof onCreateSite === "function";
  const roleFilterOptions = useMemo(
    () =>
      (roleKeys || [])
        .filter((role) => role?.value)
        .map((role) => ({
          value: role.value,
          label: formattedRoleAddress(role.value),
        })),
    [roleKeys],
  );

  if (token) {
    return isLoading ? (
      <Loading color="#F5F5F5" />
    ) : (
      <div className="w-full">
        <Card
          className={
            isSettingsList
              ? "table border-0 shadow-none bg-white rounded-lg"
              : "table"
          }
        >
          <StandardTable
            fluidColCount={showEmptyState ? 1 : isSettingsList ? 2 : 3}
            fixedColWidth={showEmptyState ? 0 : isSettingsList ? 0 : 100}
            containerClassName={
              showEmptyState
                ? "table-standard-container--list-empty"
                : undefined
            }
            columnAlignments={
              showEmptyState
                ? ["left"]
                : isSettingsList
                  ? ["left", "left"]
                  : ["left", "left", "left", "right"]
            }
            topBar={
              hasAnyAddresses ? (
                <section className="table-standard-top-bar">
                  <div className="table-standard-top-bar-inner">
                    <div className="flex gap-2 items-center flex-wrap flex-1 min-w-0">
                      <div className="search-input-wrapper">
                        <i
                          className="fas fa-search search-input-icon"
                          aria-hidden
                        />
                        <input
                          type="text"
                          className="search-input"
                          value={keywordFilter}
                          placeholder={
                            isSettingsList
                              ? t("settingsSites.searchPlaceholder")
                              : `${t("Search")} (${t("Name")}, ${t("Address")})`
                          }
                          onChange={(e) => setKeywordFilter(e.target.value)}
                        />
                      </div>
                      <StandardTableFilterDropdown
                        label={t("Role")}
                        value={listRoleFilter?.selectedList?.[0]?.value || ""}
                        options={roleFilterOptions}
                        allLabel={
                          isSettingsList
                            ? t("settingsSites.roleFilterAll")
                            : t("All roles")
                        }
                        alwaysShowLabelPrefix={isSettingsList}
                        onChange={(nextValue) => {
                          if (!nextValue) {
                            setListRoleFilter({});
                            return;
                          }
                          const selected = (roleKeys || []).find(
                            (role) => role?.value === nextValue,
                          );
                          if (selected) {
                            setListRoleFilter({ selectedList: [selected] });
                          }
                        }}
                      />
                    </div>
                    <div className="create-button-wrapper">
                      <button
                        type="button"
                        className="create-button"
                        onClick={() => {
                          if (typeof onCreateSite === "function") {
                            onCreateSite();
                            return;
                          }
                          setShowPresetModal(true);
                        }}
                      >
                        {isSettingsList
                          ? t("settingsSites.createNewSiteButton")
                          : t("Create a site")}
                      </button>
                    </div>
                  </div>
                </section>
              ) : null
            }
            header={
              hasAnyAddresses ? (
                <AddressHeader
                  variant={isSettingsList ? "settings" : "default"}
                  onSort={(field, order) => {
                    setSortByField(field);
                    setOrderSortByField(order);
                  }}
                  sortByField={sortByField}
                  orderSortByField={orderSortByField}
                />
              ) : null
            }
            bottom={
              hasAnyAddresses ? (
                <div className="table-standard-bottom table-pagination-bottom">
                  <Pagination
                    totalEntry={totalEntry}
                    recordPerPage={recordPerPage}
                    setRecordPerPage={setRecordPerPage}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    resultPage={resultPage}
                    setResultPage={setResultPage}
                  />
                </div>
              ) : null
            }
          >
            {hasAddresses ? (
              tableData.map((address) => {
                if (!address) return null;
                return (
                  <Fragment key={address._id}>
                    {modalEdit && formData._id === address._id ? (
                      <AddressForm
                        mode="edit"
                        data={formData}
                        setData={setFormData}
                        tableData={tableData}
                        setTableData={setTableData}
                        isOpen={modalEdit}
                        setIsOpen={setModalEdit}
                      />
                    ) : null}
                    <AddressRow
                      data={address}
                      tableData={tableData}
                      setTableData={setTableData}
                      setIsDeleted={setIsDeleted}
                      formData={formData}
                      setFormData={setFormData}
                      setModalEdit={setModalEdit}
                      onOpenSite={onOpenSite}
                      variant={isSettingsList ? "settings" : "default"}
                    />
                  </Fragment>
                );
              })
            ) : hasAnyAddresses && hasActiveFilters ? (
              <div className="table-standard-row table-standard-row--empty">
                <div
                  className="table-standard-row-cell"
                  style={{
                    gridColumn: isSettingsList ? "span 2" : "span 3",
                  }}
                >
                  {t("No results match your search")}
                </div>
                {!isSettingsList && (
                  <div className="table-standard-row-cell table-standard-row-actions" />
                )}
              </div>
            ) : (
              <EmptyState
                title={t("emptyState.noSite")}
                description={t("emptyState.hintSite")}
                buttonText={t("Create a site")}
                onButtonClick={() => {
                  if (typeof onCreateSite === "function") {
                    onCreateSite();
                    return;
                  }
                  setShowPresetModal(true);
                }}
              />
            )}
            {modalCreate && (
              <AddressForm
                mode="create"
                data={addFormData}
                setData={setAddFormData}
                defaultData={defaultData}
                tableData={tableData}
                setTableData={setTableData}
                totalEntry={totalEntry}
                setTotalEntry={setTotalEntry}
                isOpen={modalCreate}
                setIsOpen={setModalCreate}
              />
            )}
          </StandardTable>
        </Card>

        {/* Modale de sélection de modèle */}
        <Modal
          isOpen={showPresetModal}
          toggle={() => setShowPresetModal(false)}
          modalClassName="preset-modal"
        >
          <ModalHeader toggle={() => setShowPresetModal(false)}>
            <h4 className="font-semibold text-2xl">{t("Create a site")}</h4>
          </ModalHeader>
          <ModalBody style={{ padding: "24px" }}>
            <div style={{ marginBottom: "24px" }}>
              <h5 className="font-semibold text-lg mb-3">
                {t("Choose from a template")}
              </h5>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                {PRESETS.map((p) => (
                  <button
                    key={p.key}
                    onClick={() => {
                      openPreset(p);
                      setShowPresetModal(false);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "12px 16px",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      backgroundColor: "#fff",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      textAlign: "left",
                      width: "100%",
                      height: "48px",
                      boxSizing: "border-box",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "#f5f5f5";
                      e.target.style.borderColor = "#d0d0d0";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "#fff";
                      e.target.style.borderColor = "#e0e0e0";
                    }}
                  >
                    <span
                      style={{
                        fontSize: "24px",
                        lineHeight: "24px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {p.icon}
                    </span>
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: 500,
                        lineHeight: "20px",
                      }}
                    >
                      {p.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            <div
              style={{
                marginTop: "24px",
                paddingTop: "24px",
                borderTop: "1px solid #e0e0e0",
              }}
            >
              <h5 className="font-semibold text-lg mb-3">
                {t("Create a custom site")}
              </h5>
              <button
                onClick={() => {
                  openCustom();
                  setShowPresetModal(false);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "12px 16px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  textAlign: "left",
                  width: "100%",
                  height: "48px",
                  boxSizing: "border-box",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#f5f5f5";
                  e.target.style.borderColor = "#d0d0d0";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#fff";
                  e.target.style.borderColor = "#e0e0e0";
                }}
              >
                <i
                  className="fas fa-plus-circle"
                  style={{
                    fontSize: "24px",
                    lineHeight: "24px",
                    color: "var(--akanthas-cyan)",
                    display: "flex",
                    alignItems: "center",
                  }}
                ></i>
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: "20px",
                  }}
                >
                  {t("Create a custom site")}
                </span>
              </button>
            </div>
          </ModalBody>
        </Modal>
      </div>
    );
  }
};

export default Address;
